// Badge "Sisa Waktu" kanonis + drill-down rincian jadwal proyek.
// Seluruh angka berasal dari field `schedule` backend (project_schedule_engine,
// Asia/Jakarta). Komponen ini TIDAK menghitung selisih tanggal — hanya merender.
import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

const STATUS_LABEL = {
  on_track: "Dalam jadwal",
  not_started: "Belum dimulai",
  due_today: "Berakhir hari ini",
  overdue: "Terlambat — melewati tanggal selesai kontrak",
  completed_early: "Selesai lebih awal",
  completed_on_time: "Selesai tepat waktu",
  completed_late: "Selesai terlambat",
  completed_missing_date: "Tanggal penyelesaian belum diisi",
  no_end_date: "Tanggal selesai belum diisi",
  no_schedule: "Jadwal belum diisi",
  not_applicable: "Tidak berlaku",
  cancelled: "Dibatalkan",
};

const fmtD = (iso) => {
  if (!iso) return "—";
  const d = new Date(String(iso).slice(0, 10) + "T00:00:00");
  return isNaN(d.getTime()) ? "—"
    : d.toLocaleDateString("id-ID", { day: "numeric", month: "short", year: "numeric" });
};

const fmtN = (n) => (n == null ? "—" : `${n} hari`);

function badgeView(s) {
  const st = s?.schedule_status;
  const pill = "inline-flex items-center gap-0.5 px-1.5 py-0.5 text-[10px] font-semibold border tabular-nums";
  if (!s || st === "not_applicable" || st === "cancelled")
    return <span className="text-slate-300">—</span>;
  if (st === "no_end_date" || st === "no_schedule")
    return <span className="text-slate-400 italic text-[10px]">Belum diset</span>;
  if (st === "completed_missing_date")
    return <span className="text-slate-400 italic text-[10px]">Tanggal penyelesaian belum diisi</span>;
  if (st === "completed_late")
    return <span className={`${pill} font-bold border-red-600 bg-red-600 text-white`}>−{Math.abs(s.completed_delta_days ?? 0)}d lewat</span>;
  if (st === "completed_on_time")
    return <span className={`${pill} font-bold border-slate-500 bg-slate-100 text-slate-800`}>Tepat waktu</span>;
  if (st === "completed_early")
    return <span className={`${pill} border-emerald-400 bg-emerald-50 text-emerald-700`}>+{s.completed_delta_days}d cepat</span>;
  if (st === "overdue")
    return <span className={`${pill} font-bold border-red-600 bg-red-600 text-white`}>−{s.overdue_days}d lewat</span>;
  if (st === "due_today")
    return <span className={`${pill} font-bold border-rose-500 bg-rose-100 text-rose-800`}>Hari ini</span>;
  const n = s.remaining_days ?? 0;
  const cls = n <= 7 ? "border-rose-400 bg-rose-50 text-rose-700"
    : n <= 30 ? "border-amber-400 bg-amber-50 text-amber-700"
    : "border-slate-300 bg-white text-slate-700";
  return <span className={`${pill} ${cls}`}>{n}d</span>;
}

export function SisaWaktuBadge({ schedule: s, testid }) {
  const [open, setOpen] = useState(false);
  if (!s) return <span className="text-slate-300" data-testid={testid}>—</span>;
  const st = s.schedule_status;
  const badge = badgeView(s);
  if (["not_applicable", "cancelled"].includes(st)) return <span data-testid={testid}>{badge}</span>;
  const rows = [
    ["Tanggal Mulai", fmtD(s.start_date), "mulai"],
    ["Tanggal Selesai", fmtD(s.end_date), "selesai"],
    ["Tanggal Acuan", `${fmtD(s.effective_date)} (${s.timezone || "Asia/Jakarta"})`, "acuan"],
    ["Total Durasi", fmtN(s.total_duration_days), "durasi"],
    ["Hari Berjalan", fmtN(s.elapsed_days), "berjalan"],
    ["Sisa Waktu", fmtN(s.remaining_days), "sisa"],
    ["Hari Keterlambatan", s.overdue_days ? `${s.overdue_days} hari` : "0 hari", "overdue"],
    ...(s.completed_date ? [["Tanggal Selesai Aktual", fmtD(s.completed_date), "selesai-aktual"]] : []),
    ["Status Jadwal", s.completed_label || STATUS_LABEL[st] || st || "—", "status"],
  ];
  return (
    <>
      <button type="button" data-testid={testid} onClick={() => setOpen(true)}
        title="Klik untuk rincian perhitungan jadwal (sumber kanonis backend, Asia/Jakarta)"
        className="cursor-pointer hover:opacity-80 transition-opacity focus:outline-none focus-visible:ring-2 focus-visible:ring-[#FF5A1F]">
        {badge}
      </button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-sm" data-testid="schedule-detail-dialog">
          <DialogHeader>
            <DialogTitle className="text-[#0B1D3A]">Rincian Sisa Waktu Proyek</DialogTitle>
          </DialogHeader>
          <div className="divide-y divide-slate-100 text-xs">
            {rows.map(([label, val, k]) => (
              <div key={k} className="flex items-center justify-between py-1.5 gap-3">
                <span className="text-slate-500">{label}</span>
                <span className="font-semibold tabular-nums text-[#0B1D3A] text-right" data-testid={`schedule-detail-${k}`}>{val}</span>
              </div>
            ))}
          </div>
          <div className="mt-2 text-[10px] text-slate-400 italic" data-testid="schedule-detail-source">
            Sumber: mesin jadwal kanonis backend (project_schedule_engine) — seluruh halaman memakai angka yang sama.
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
